import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Eye, EyeOff, AlertCircle, Check, ArrowLeft } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import Button from '../../components/shared/Button/Button';
import FormField from '../../components/shared/FormField/FormField';
import Input from '../../components/shared/Input/Input';

const RegisterPage = () => {
  const [form, setForm] = useState({
    username: '',
    email: '',
    first_name: '',
    last_name: '',
    phone_number: '',
    password: '',
    password_confirm: '',
  });
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { register } = useAuth();
  const navigate = useNavigate();

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (form.password !== form.password_confirm) {
      setError('Passwords do not match.');
      return;
    }
    setLoading(true);
    setError('');
    const success = await register(form);
    if (success) {
      navigate('/login');
    } else {
      setError('Registration failed. Please try again.');
    }
    setLoading(false);
  };

  return (
    <div className="flex min-h-screen flex-col bg-white lg:flex-row">
      <aside className="relative flex flex-col justify-between overflow-hidden bg-brand-900 px-6 py-8 text-white lg:w-1/2 lg:px-12 lg:py-12">
        <div
          aria-hidden
          className="pointer-events-none absolute inset-0 bg-[radial-gradient(ellipse_at_top_left,_rgba(255,228,230,0.15),_transparent_60%),radial-gradient(ellipse_at_bottom_right,_rgba(225,29,72,0.25),_transparent_55%)]"
        />
        <div className="relative">
          <Link
            to="/"
            className="inline-flex rounded-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-4 focus-visible:ring-offset-brand-900"
          >
            {/* Slanted wordmark, as painted on the shuttles */}
            <span className="-skew-x-6 bg-livery-600 px-3 py-1.5 font-signage text-[1.75rem] font-extrabold italic uppercase leading-none tracking-wide text-white">
              Cruiser
            </span>
          </Link>
        </div>

        <div className="relative hidden max-w-md lg:block">
          <h2 className="text-3xl font-bold leading-tight">
            Run every route from one connected control room.
          </h2>
          <ul className="mt-8 space-y-4 text-base text-brand-100">
            {[
              'Trips, bookings, parcels — all in one timeline',
              'Real-time financial visibility',
              'Designed for fleet operators of any size',
            ].map((item) => (
              <li key={item} className="flex items-start gap-3">
                <span className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-brand-700/60 text-white">
                  <Check className="h-3.5 w-3.5" aria-hidden />
                </span>
                {item}
              </li>
            ))}
          </ul>
        </div>

        <p className="relative hidden text-sm text-brand-200/80 lg:block">
          © {new Date().getFullYear()} Cruiser
        </p>
      </aside>

      <main className="relative flex flex-1 items-center justify-center px-6 py-10 sm:px-10">
        <Link
          to="/"
          className="absolute left-6 top-6 inline-flex items-center gap-1.5 text-sm font-medium text-slate-600 hover:text-slate-900 sm:left-10 sm:top-10"
        >
          <ArrowLeft className="h-4 w-4" aria-hidden /> Back to home
        </Link>

        <div className="w-full max-w-xl">
          <h1 className="text-3xl font-bold tracking-tight text-slate-900">
            Create your account
          </h1>
          <p className="mt-2 text-base text-slate-600">
            Join Cruiser to manage trips, parcels, and finances in one place.
          </p>

          <form onSubmit={handleSubmit} className="mt-8 flex flex-col gap-5">
            {error && (
              <div className="flex items-start gap-2.5 rounded-lg border border-rose-200 bg-rose-50 px-4 py-3 text-sm font-medium text-rose-800">
                <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden />
                <span>{error}</span>
              </div>
            )}

            <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
              <FormField label="First name" name="first_name" required>
                <Input
                  id="first_name"
                  name="first_name"
                  value={form.first_name}
                  onChange={handleChange}
                  required
                />
              </FormField>
              <FormField label="Last name" name="last_name" required>
                <Input
                  id="last_name"
                  name="last_name"
                  value={form.last_name}
                  onChange={handleChange}
                  required
                />
              </FormField>
            </div>

            <FormField label="Username" name="username" required>
              <Input
                id="username"
                name="username"
                value={form.username}
                onChange={handleChange}
                autoComplete="username"
                required
              />
            </FormField>

            <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
              <FormField label="Email" name="email">
                <Input
                  id="email"
                  name="email"
                  type="email"
                  value={form.email}
                  onChange={handleChange}
                  autoComplete="email"
                />
              </FormField>
              <FormField label="Phone number" name="phone_number" required>
                <Input
                  id="phone_number"
                  name="phone_number"
                  type="tel"
                  value={form.phone_number}
                  onChange={handleChange}
                  autoComplete="tel"
                  required
                />
              </FormField>
            </div>

            <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
              <FormField label="Password" name="password" required>
                <Input
                  id="password"
                  name="password"
                  type={showPassword ? 'text' : 'password'}
                  value={form.password}
                  onChange={handleChange}
                  autoComplete="new-password"
                  required
                  trailingIcon={
                    <button
                      type="button"
                      onClick={() => setShowPassword((v) => !v)}
                      aria-label={showPassword ? 'Hide password' : 'Show password'}
                      className="pointer-events-auto rounded p-1 text-slate-500 hover:bg-slate-100 hover:text-slate-700"
                    >
                      {showPassword ? (
                        <EyeOff className="h-4 w-4" />
                      ) : (
                        <Eye className="h-4 w-4" />
                      )}
                    </button>
                  }
                />
              </FormField>
              <FormField label="Confirm password" name="password_confirm" required>
                <Input
                  id="password_confirm"
                  name="password_confirm"
                  type={showPassword ? 'text' : 'password'}
                  value={form.password_confirm}
                  onChange={handleChange}
                  autoComplete="new-password"
                  required
                />
              </FormField>
            </div>

            <Button type="submit" loading={loading} fullWidth>
              {loading ? 'Creating account…' : 'Create account'}
            </Button>
          </form>

          <p className="mt-6 text-center text-sm text-slate-600">
            Already have an account?{' '}
            <Link
              to="/login"
              className="font-semibold text-brand-700 hover:underline"
            >
              Sign in
            </Link>
          </p>
        </div>
      </main>
    </div>
  );
};

export default RegisterPage;
